//import e from 'express';

let ampStatus = {
  preRelease: '1',
  notRecommendedForNewDesigns: '4',
  lastTimeBuy: '5',
  obsolete: '6',
};

export function reasonsOpampMarginal(limits, opAmp, applyAdditionalCriteria) {
  let reasons = [];
  let recommendedGBW = limits.maxGbw;
  let recommendedRoutOverGBW = limits.minROutOverGBW;
  let maxiBias = limits.minIBias;

  if (recommendedGBW > parseFloat(opAmp.gbw)) {
    reasons.push({ messageId: 'gbw-low' });
  }

  if (opAmp.status === ampStatus.preRelease) {
    reasons.push({ messageId: 'prerelease' });
  } else if (opAmp.status === ampStatus.notRecommendedForNewDesigns) {
    reasons.push({ messageId: 'not-recommended-new-designs' });
  } else if (opAmp.status === ampStatus.lastTimeBuy) {
    reasons.push({ messageId: 'last-time-buy' });
  }

  let routOverGBW = parseFloat(opAmp.rOut) / parseFloat(opAmp.gbw);
  if (recommendedRoutOverGBW < routOverGBW || polesZeroesMarginal(limits, opAmp)) {
    reasons.push({ messageId: 'rout-high' });
  }

  if (opAmp.iBias > maxiBias) {
    reasons.push({ messageId: 'ibias_high' });
  }

  if (typeof applyAdditionalCriteria === 'function') {
    if (applyAdditionalCriteria(opAmp) === 'allowed') {
      reasons.push({ messageId: 'caller-request' });
    }
  }

  return reasons;
}

// The following function flags opamps with poles that are too close to the frequency range of interest.
// Poles near the operating region can result in strange response curves.
export function polesZeroesMarginal(limits, opAmp) {
  let result = false;
  const FUDGE_FACTOR = 10;
  if ('polesZeroes' in opAmp) {
    let minPole = opAmp.polesZeroes.p.find((value) => {
      return value > 10;
    });
    result = (opAmp.rOut * FUDGE_FACTOR) / minPole > limits.minROutOverGBW;
  }
  return result;
}

export function reasonsOpampNotAllowed(posVS, negVS, limits, rcLimitsError, opAmp, applyAdditionalCriteria) {
  let minGbwAllowed = limits.maxGbw;
  let maxRoutoverGBW = limits.minROutOverGBW;
  let maxiBias = limits.minIBias;
  let minSlewRate = limits.maxSlewRate;
  let stabilityGain = limits.minSpecialGain;

  let reasons = [];
  let refV = negVS === 0 ? posVS / 2 : 0;
  let supplySpan = posVS - negVS;

  if (opAmp.partNumber === null || opAmp.partNumber === '') {
    reasons.push({ messageId: 'blank-opamp-name' });
    reasons.push({ messageId: 'not-available-in-database' });
  } else {
    if (rcLimitsError) {
      reasons.push({ messageId: 'components-out-of-range' });
    }
    if (opAmp.status === ampStatus.obsolete) {
      reasons.push({ messageId: 'obsolete' });
    }
    if (
      (opAmp.feedbackMethod && opAmp.feedbackMethod.indexOf('Current') > -1) ||
      (opAmp.architecture && opAmp.architecture.indexOf('Current Feedback') > -1)
    ) {
      reasons.push({ messageId: 'current-feedback-unsupported' });
    }
    if (parseFloat(opAmp.minStableGain) > stabilityGain) {
      if (!!opAmp.spiceModels && opAmp.spiceModels.compPin) {
        reasons.push({ messageId: 'under-min-stable-gain-no-cap', value: opAmp.minStableGain });
      } else {
        reasons.push({ messageId: 'under-min-stable-gain', value: opAmp.minStableGain });
      }
    }
    if (minGbwAllowed > parseFloat(opAmp.gbw)) {
      reasons.push({ messageId: 'gbw-too-low' });
    }

    if (supplySpan < parseFloat(opAmp.minSupplyVoltage)) {
      reasons.push({ messageId: 'supply-voltage-low' });
    } else if (supplySpan > parseFloat(opAmp.maxSupplyVoltage)) {
      reasons.push({ messageId: 'supply-voltage-high' });
    }
    if (opAmp.rOut && parseFloat(opAmp.rOut) > 0) {
      let routOverGBW = parseFloat(opAmp.rOut) / parseFloat(opAmp.gbw);
      if (maxRoutoverGBW < routOverGBW) {
        reasons.push({ messageId: 'output-impedence-high' });
      }
    } else if (!opAmp.rOut || parseFloat(opAmp.rOut) === 0) {
      let proxyRout = 0.3 / parseFloat(opAmp.supplyCurrent);
      if (maxRoutoverGBW < proxyRout / parseFloat(opAmp.gbw)) {
        reasons.push({ messageId: 'output-impedence-high' });
      }
    }
    if (parseFloat(opAmp.slewRate) < minSlewRate) {
      reasons.push({ messageId: 'slew-rate-low' });
    }
    // We'll require at least -100mV input and output headroom
    if (reasons.length === 0) {
      if (posVS - parseFloat(opAmp.inHeadroomPlus) - 0.1 <= refV) {
        reasons.push({ messageId: 'not-enough-positive-input-range' });
      } else if (posVS - parseFloat(opAmp.outHeadroomPlus) - 0.1 <= refV) {
        reasons.push({ messageId: 'not-enough-positive-output-range' });
      } else if (negVS - parseFloat(opAmp.inHeadroomMinus) + 0.1 >= refV) {
        reasons.push({ messageId: 'not-enough-negative-input-range' });
      } else if (negVS - parseFloat(opAmp.outHeadroomMinus) + 0.1 >= refV) {
        reasons.push({ messageId: 'not-enough-negative-output-range' });
      }
    }
    // Keep output voltage from getting too big with interaction of bias current and input resistors
    if (parseFloat(opAmp.iBias) > maxiBias) {
      reasons.push({ messageId: 'ibias_high' });
    }
  }

  if (typeof applyAdditionalCriteria === 'function') {
    if (applyAdditionalCriteria(opAmp) === 'notAllowed') {
      reasons.push({ messageId: 'caller-request' });
    }
  }

  return reasons;
}
